import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@personic/shared'

// =============================================================================
// TYPES
// =============================================================================

export interface EmailTemplateListItem {
  email_type: string
  subject_template: string
  is_active: boolean
  has_custom_body: boolean
  last_edited_by_id: string | null
  updated_at: string | null
}

export interface EmailTemplateDetail {
  email_type: string
  subject_template: string
  html_body: string
  default_subject: string
  default_html: string
  is_active: boolean
  variables_schema: Record<string, string> | null
  last_edited_by_id: string | null
  last_tested_at: string | null
  notes: string | null
  created_at: string
  updated_at: string
}

export interface EmailConfigResponse {
  provider: string
  sender_address: string
  sender_name: string
  base_url: string
  health: 'healthy' | 'degraded' | 'not_configured'
}

export interface EmailAuditEntry {
  id: string
  email_type: string
  recipient_email: string
  subject: string
  status: string
  provider: string | null
  sent_at: string | null
  created_at: string
  error_message: string | null
  tags: string[] | null
}

export interface EmailAuditListResponse {
  items: EmailAuditEntry[]
  total: number
  page: number
  page_size: number
}

interface EmailTemplateUpdate {
  subject_template?: string
  html_body?: string
  notes?: string
}

interface UseEmailAuditOptions {
  page?: number
  page_size?: number
  email_type?: string
  status?: string
  recipient_email?: string
}

// =============================================================================
// QUERIES
// =============================================================================

/**
 * Fetch all email templates
 */
export function useEmailTemplates() {
  return useQuery<EmailTemplateListItem[]>({
    queryKey: ['admin-email-templates'],
    queryFn: async () => {
      const response = await api.get('/admin/emails/templates')
      return response.data
    },
  })
}

/**
 * Fetch single email template with details
 */
export function useEmailTemplate(emailType: string | null) {
  return useQuery<EmailTemplateDetail>({
    queryKey: ['admin-email-template', emailType],
    queryFn: async () => {
      if (!emailType) throw new Error('Email type required')
      const response = await api.get(`/admin/emails/templates/${emailType}`)
      return response.data
    },
    enabled: !!emailType,
  })
}

/**
 * Fetch email service configuration
 */
export function useEmailConfig() {
  return useQuery<EmailConfigResponse>({
    queryKey: ['admin-email-config'],
    queryFn: async () => {
      const response = await api.get('/admin/emails/config')
      return response.data
    },
  })
}

/**
 * Fetch email audit log with pagination and filters
 */
export function useEmailAudit(options: UseEmailAuditOptions = {}) {
  const { page = 1, page_size = 50, email_type, status, recipient_email } = options

  return useQuery<EmailAuditListResponse>({
    queryKey: ['admin-email-audit', { page, page_size, email_type, status, recipient_email }],
    queryFn: async () => {
      const params = new URLSearchParams()
      params.append('page', String(page))
      params.append('page_size', String(page_size))
      if (email_type) params.append('email_type', email_type)
      if (status) params.append('status', status)
      if (recipient_email) params.append('recipient_email', recipient_email)

      const response = await api.get(`/admin/emails/audit?${params}`)
      return response.data
    },
  })
}

// =============================================================================
// MUTATIONS
// =============================================================================

/**
 * Update email template
 */
export function useUpdateTemplate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ emailType, update }: { emailType: string; update: EmailTemplateUpdate }) => {
      const response = await api.put(`/admin/emails/templates/${emailType}`, update)
      return response.data
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin-email-templates'] })
      queryClient.invalidateQueries({ queryKey: ['admin-email-template', variables.emailType] })
    },
  })
}

/**
 * Activate custom template
 */
export function useActivateTemplate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (emailType: string) => {
      const response = await api.post(`/admin/emails/templates/${emailType}/activate`)
      return response.data
    },
    onSuccess: (_data, emailType) => {
      queryClient.invalidateQueries({ queryKey: ['admin-email-templates'] })
      queryClient.invalidateQueries({ queryKey: ['admin-email-template', emailType] })
    },
  })
}

/**
 * Deactivate custom template (revert to default)
 */
export function useDeactivateTemplate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (emailType: string) => {
      const response = await api.post(`/admin/emails/templates/${emailType}/deactivate`)
      return response.data
    },
    onSuccess: (_data, emailType) => {
      queryClient.invalidateQueries({ queryKey: ['admin-email-templates'] })
      queryClient.invalidateQueries({ queryKey: ['admin-email-template', emailType] })
    },
  })
}

/**
 * Preview template with sample data
 */
export function usePreviewTemplate() {
  return useMutation({
    mutationFn: async (emailType: string) => {
      const response = await api.post(`/admin/emails/templates/${emailType}/preview`)
      return response.data
    },
  })
}

/**
 * Send test email
 */
export function useTestTemplate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (emailType: string) => {
      const response = await api.post(`/admin/emails/templates/${emailType}/test`)
      return response.data
    },
    onSuccess: (_data, emailType) => {
      queryClient.invalidateQueries({ queryKey: ['admin-email-template', emailType] })
      queryClient.invalidateQueries({ queryKey: ['admin-email-audit'] })
    },
  })
}

/**
 * Reset template to default
 */
export function useResetTemplate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (emailType: string) => {
      const response = await api.post(`/admin/emails/templates/${emailType}/reset`)
      return response.data
    },
    onSuccess: (_data, emailType) => {
      queryClient.invalidateQueries({ queryKey: ['admin-email-templates'] })
      queryClient.invalidateQueries({ queryKey: ['admin-email-template', emailType] })
    },
  })
}
